/**
 * O que faz: lê as datas necessárias para o tempo até fechar: vendas do período, primeiro contato
 *            de cada lead e se ele já tinha comprado antes do período (recompra).
 * Onde usa: hooks useTempoFechamentoVendas (aba /vendas e cartão do dashboard).
 * Conexões: Supabase autenticado; o cálculo fica em src/lib/vendas/tempoFechamento.ts.
 * Tabelas: vendas_realizadas, leads e metricas_conversas — somente SELECT, protegido por RLS.
 * Data: 05/10/2026.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  escolherInicioJornada,
  type JornadaLeadTempoFechamento,
  type VendaParaTempoFechamento,
} from '@/lib/vendas/tempoFechamento';

const TAMANHO_PAGINA = 1000;
// Lotes de IDs vão na URL do filtro "in": 150 UUIDs mantêm a URL bem abaixo do limite do servidor.
const TAMANHO_LOTE_LEADS = 150;
// Até 3 lotes ao mesmo tempo, o mesmo equilíbrio usado nas origens de anúncio de Vendas.
const LOTES_EM_PARALELO = 3;
const MENSAGEM_ERRO = 'Não foi possível calcular o tempo até fechar. Tente atualizar os dados.';

interface LinhaVenda { lead_id: string; data_venda: string }
interface LinhaLead { id: string; created_at: string | null; import: boolean | null }
interface LinhaMetricaConversa { lead_id: string; primeiro_contato_em: string | null }

// Paginação para não parar silenciosamente no limite de 1000 linhas do PostgREST.
async function lerTodasPaginas<T>(criarConsulta: (de: number, ate: number) => PromiseLike<{ data: unknown; error: unknown }>): Promise<T[]> {
  const registros: T[] = [];
  let inicio = 0;
  while (true) {
    const { data, error } = await criarConsulta(inicio, inicio + TAMANHO_PAGINA - 1);
    // Resultado parcial mostraria uma mediana errada como se fosse confirmada.
    if (error) throw new Error(MENSAGEM_ERRO);
    const pagina = (data ?? []) as T[];
    registros.push(...pagina);
    if (pagina.length < TAMANHO_PAGINA) return registros;
    inicio += pagina.length;
  }
}

async function processarEmLotes<R>(ids: string[], buscarLote: (lote: string[]) => Promise<R[]>): Promise<R[]> {
  const lotes: string[][] = [];
  for (let inicio = 0; inicio < ids.length; inicio += TAMANHO_LOTE_LEADS) lotes.push(ids.slice(inicio, inicio + TAMANHO_LOTE_LEADS));
  const resultados: R[] = [];
  for (let indice = 0; indice < lotes.length; indice += LOTES_EM_PARALELO) {
    const grupo = await Promise.all(lotes.slice(indice, indice + LOTES_EM_PARALELO).map(buscarLote));
    grupo.forEach((linhas) => resultados.push(...linhas));
  }
  return resultados;
}

/**
 * Vendas do CRM no período (para o cartão do dashboard, que não carrega a lista de Vendas).
 */
export async function buscarVendasPeriodoTempoFechamento(
  clinicaId: string,
  inicio: Date | null,
  fim: Date | null,
  signal?: AbortSignal,
): Promise<VendaParaTempoFechamento[]> {
  // Tabela: vendas_realizadas | Operação: SELECT.
  // Campos: lead_id (para achar a jornada) e data_venda (fim da contagem). Sem valor nem dados pessoais.
  // Filtros: clinica_id e data_venda dentro do período — mesma regra de data do faturamento.
  const linhas = await lerTodasPaginas<LinhaVenda>((de, ate) => {
    let consulta = supabase.from('vendas_realizadas' as never)
      .select('lead_id, data_venda')
      .eq('clinica_id', clinicaId)
      .order('data_venda', { ascending: true }).order('id', { ascending: true }).range(de, ate);
    if (inicio) consulta = consulta.gte('data_venda', inicio.toISOString());
    if (fim) consulta = consulta.lte('data_venda', fim.toISOString());
    if (signal) consulta = consulta.abortSignal(signal);
    return consulta;
  });
  return linhas.map((linha) => ({ leadId: linha.lead_id, dataVenda: linha.data_venda }));
}

/**
 * Para cada lead: início da jornada (primeiro contato) e se comprou antes do período.
 */
export async function buscarJornadaLeadsTempoFechamento(
  clinicaId: string,
  leadIds: string[],
  inicioPeriodo: Date | null,
  signal?: AbortSignal,
): Promise<Map<string, JornadaLeadTempoFechamento>> {
  const ids = [...new Set(leadIds.filter(Boolean))];
  const mapa = new Map<string, JornadaLeadTempoFechamento>();
  if (!clinicaId || ids.length === 0) return mapa;

  const [leads, metricas, comprasAnteriores] = await Promise.all([
    // Tabela: leads | Operação: SELECT.
    // Campos: id, created_at (entrada no CRM) e import (lead importado tem created_at = data da importação).
    // Filtros: clinica_id + lista de leads das vendas. Sem nome/telefone: não são necessários aqui.
    processarEmLotes<LinhaLead>(ids, async (lote) => {
      let consulta = supabase.from('leads').select('id, created_at, import').eq('clinica_id', clinicaId).in('id', lote);
      if (signal) consulta = consulta.abortSignal(signal);
      const { data, error } = await consulta;
      if (error) throw new Error(MENSAGEM_ERRO);
      return (data ?? []) as unknown as LinhaLead[];
    }),
    // Tabela: metricas_conversas | Operação: SELECT.
    // Campos: lead_id e primeiro_contato_em (primeira mensagem confirmada, de qualquer lado).
    // Filtros: clinica_id + lista de leads, só quem já tem a data. Sem texto de mensagens.
    processarEmLotes<LinhaMetricaConversa>(ids, async (lote) => {
      let consulta = supabase.from('metricas_conversas' as never)
        .select('lead_id, primeiro_contato_em')
        .eq('clinica_id', clinicaId).in('lead_id', lote).not('primeiro_contato_em', 'is', null);
      if (signal) consulta = consulta.abortSignal(signal);
      const { data, error } = await consulta;
      if (error) throw new Error(MENSAGEM_ERRO);
      return (data ?? []) as unknown as LinhaMetricaConversa[];
    }),
    // Tabela: vendas_realizadas | Operação: SELECT.
    // Campos: lead_id apenas. Filtros: clinica_id, leads da lista e data_venda ANTES do período.
    // Uso: identifica recompra. Sem período inicial (filtro "Máximo") toda venda é a primeira.
    inicioPeriodo
      ? processarEmLotes<{ lead_id: string }>(ids, (lote) => lerTodasPaginas<{ lead_id: string }>((de, ate) => {
        let consulta = supabase.from('vendas_realizadas' as never)
          .select('lead_id')
          .eq('clinica_id', clinicaId).in('lead_id', lote).lt('data_venda', inicioPeriodo.toISOString())
          .order('id', { ascending: true }).range(de, ate);
        if (signal) consulta = consulta.abortSignal(signal);
        return consulta;
      }))
      : Promise.resolve([] as Array<{ lead_id: string }>),
  ]);

  const leadPorId = new Map(leads.map((lead) => [lead.id, lead]));
  const primeiroContatoPorLead = new Map(metricas.map((linha) => [linha.lead_id, linha.primeiro_contato_em]));
  const compradoresAnteriores = new Set(comprasAnteriores.map((linha) => linha.lead_id));

  ids.forEach((leadId) => {
    const lead = leadPorId.get(leadId);
    mapa.set(leadId, {
      inicioJornada: escolherInicioJornada({
        primeiroContatoEm: primeiroContatoPorLead.get(leadId) ?? null,
        criadoEm: lead?.created_at ?? null,
        importado: lead?.import === true,
      }),
      comprouAntes: compradoresAnteriores.has(leadId),
    });
  });
  return mapa;
}
